import React from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, TextInputProps, ViewStyle, TextStyle, Platform } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

export interface PasswordInputProps extends Omit<TextInputProps, 'secureTextEntry'> {
  label?: string;
  showPassword: boolean;
  onToggleVisibility: () => void;
  containerStyle?: ViewStyle;
  labelStyle?: TextStyle;
  inputStyle?: TextStyle;
  errorMessage?: string;
}

export const PasswordInput: React.FC<PasswordInputProps> = ({
  label,
  showPassword,
  onToggleVisibility,
  containerStyle,
  labelStyle,
  inputStyle,
  errorMessage,
  ...textInputProps
}) => {
  const styles = useThemedStyles(makeStyles);
  const { colors } = useThemeColors();

  return (
    <View style={[styles.container, containerStyle]}>
      {label && (
        <Text style={[styles.label, labelStyle]}>{label}</Text>
      )}
      <View style={styles.passwordWrapper}>
        <TextInput
          {...textInputProps}
          style={[styles.input, inputStyle, errorMessage && styles.inputError]}
          secureTextEntry={!showPassword}
          autoCapitalize="none"
          autoComplete="new-password"
        />
        <Pressable
          style={styles.eyeButton}
          onPress={onToggleVisibility}
        >
          {showPassword ? (
            <SVG_COMPONENTS.EYE_OPEN width={20} height={20} fill={colors.text.muted} />
          ) : (
            <SVG_COMPONENTS.EYE_CLOSED width={20} height={20} fill={colors.text.muted} />
          )}
        </Pressable>
      </View>
      {errorMessage && (
        <Text style={styles.errorText}>{errorMessage}</Text>
      )}
    </View>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text.primary,
    marginBottom: 8,
  },
  passwordWrapper: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    height: 44,
    borderWidth: 1,
    borderColor: semantic.border.secondary,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingRight: 44, // Space for eye button
    fontSize: 16,
    color: colors.text.primary,
    backgroundColor: semantic.background.input,
    ...Platform.select({
      web: {
        outlineStyle: 'none' as any,
      },
    }),
  },
  inputError: {
    borderColor: semantic.status.error,
  },
  eyeButton: {
    position: 'absolute',
    right: 12,
    padding: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 12,
    color: semantic.status.error,
    marginTop: 4,
  },
});

